import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  normalizeWorkingSchedule,
} from '../../../shared/booking/employee-schedule/workingSchedule';
import PrimaryButton from '../../../website/components/account-setup/shared/PrimaryButton';
import { useAccountSettingsStyleVars } from '../../../shared/hooks/useAccountSettingsStyleVars';
import EmployeeScheduleEditor from './sections/EmployeeScheduleEditor';
import LocationAssignmentField from '../../../locations/components/LocationAssignmentField';
import useCompanyLocations from '../../../locations/hooks/useCompanyLocations';
import { normalizeLocationIds } from '../../../locations/locationTypes';
import '../../../components/settings/pages/AccountSettings/AccountSettings.css';
import '../../../shared/styles/accountFieldsFlat.css';
import './styles/employee-profile-workspace.css';

const formatPhoneNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);

  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const EmployeeProfileEditor = ({
  employee,
  section = 'profile',
  status = 'active',
  onCancel,
  onSave,
  saving = false,
}) => {
  const { companyData } = useAuth();
  const cssVars = useAccountSettingsStyleVars();
  const { locations } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
  });
  const [formData, setFormData] = useState({
    displayName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    address: '',
    city: '',
    zipCode: '',
    country: '',
    state: '',
    locationIds: [],
    workingSchedule: normalizeWorkingSchedule(null, companyData?.settings?.companyHours),
  });

  useEffect(() => {
    if (!employee) return;

    setFormData({
      displayName: employee.displayName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      phone: employee.phone || '',
      title: employee.title || employee.position || '',
      address: employee.address || employee.streetAddress || '',
      city: employee.city || '',
      zipCode: employee.zipCode || employee.postalCode || '',
      country: employee.country || '',
      state: employee.state || '',
      locationIds: normalizeLocationIds(employee.locationIds),
      workingSchedule: normalizeWorkingSchedule(
        employee.workingSchedule,
        companyData?.settings?.companyHours
      ),
    });
  }, [employee, companyData?.settings?.companyHours]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'phone') {
      setFormData((prev) => ({
        ...prev,
        phone: formatPhoneNumber(value),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleScheduleDayChange = (dayKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      workingSchedule: {
        ...prev.workingSchedule,
        [dayKey]: {
          ...prev.workingSchedule[dayKey],
          [field]: value,
          ...(field === 'isWorking' && value === false ? { hasBreak: false } : {}),
        },
      },
    }));
  };

  const handleSubmit = async () => {
    const activeLocationCount = locations.filter((location) => location.active !== false).length;
    const normalizedLocationIds = normalizeLocationIds(formData.locationIds);

    if (activeLocationCount > 1 && normalizedLocationIds.length === 0) {
      window.alert('Select at least one location for this team member.');
      return;
    }

    const baseProfileData = {
      displayName: formData.displayName,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      title: formData.title,
      position: formData.title,
      address: formData.address,
      streetAddress: formData.address,
      city: formData.city,
      zipCode: formData.zipCode,
      postalCode: formData.zipCode,
      country: formData.country,
      state: formData.state,
      locationIds: normalizedLocationIds,
      status,
    };

    const saveData =
      section === 'schedule'
        ? {
            workingSchedule: normalizeWorkingSchedule(
              formData.workingSchedule,
              companyData?.settings?.companyHours
            ),
          }
        : baseProfileData;

    const didSave = await onSave(saveData);
    if (didSave) {
      onCancel?.();
    }
  };

  if (section === 'schedule') {
    return (
      <EmployeeScheduleEditor
        workingSchedule={formData.workingSchedule}
        onScheduleDayChange={handleScheduleDayChange}
        onCancel={onCancel}
        onSave={handleSubmit}
        saving={saving}
      />
    );
  }

  return (
    <div className="accountFieldsFlat" style={cssVars}>
      <section className="asCard" aria-label="Basic profile">
        <div className="asCardHeader">
          <h2>Basic profile</h2>
          <p className="sub">Name, display name, and how to reach this team member.</p>
        </div>
        <div className="asCardBody">
          <div className="asFormGrid">
            <div className="asField" style={{ gridColumn: '1 / -1' }}>
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-displayName">
                  Display name
                </label>
              </div>
              <input
                id="emp-displayName"
                className="asInput"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-firstName">
                  First name
                </label>
              </div>
              <input
                id="emp-firstName"
                className="asInput"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="given-name"
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-lastName">
                  Last name
                </label>
              </div>
              <input
                id="emp-lastName"
                className="asInput"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="family-name"
                disabled={saving}
              />
            </div>
            <div className="asField" style={{ gridColumn: '1 / -1' }}>
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-email">
                  Email
                </label>
                <span className="asHint">Managed by sign-in account</span>
              </div>
              <input
                id="emp-email"
                className="asInput"
                name="email"
                value={formData.email}
                disabled
                readOnly
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-phone">
                  Phone
                </label>
              </div>
              <input
                id="emp-phone"
                className="asInput"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(123) 456-7890"
                maxLength={14}
                disabled={saving}
              />
              <p className="asHelperText">Format: (123) 456-7890</p>
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-title">
                  Title
                </label>
              </div>
              <input
                id="emp-title"
                className="asInput"
                name="title"
                value={formData.title}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="asCard" aria-label="Address">
        <div className="asCardHeader">
          <h2>Address</h2>
          <p className="sub">Location details on file for this employee.</p>
        </div>
        <div className="asCardBody">
          <div className="asFormGrid asFormGrid--three">
            <div className="asField" style={{ gridColumn: '1 / -1' }}>
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-address">
                  Street address
                </label>
              </div>
              <input
                id="emp-address"
                className="asInput"
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-city">
                  City
                </label>
              </div>
              <input
                id="emp-city"
                className="asInput"
                name="city"
                value={formData.city}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-zipCode">
                  ZIP / postal code
                </label>
              </div>
              <input
                id="emp-zipCode"
                className="asInput"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            <div className="asField">
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-state">
                  State
                </label>
              </div>
              <input
                id="emp-state"
                className="asInput"
                name="state"
                value={formData.state}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            <div className="asField" style={{ gridColumn: '1 / -1' }}>
              <div className="asFieldLabelRow">
                <label className="asLabel" htmlFor="emp-country">
                  Country
                </label>
              </div>
              <input
                id="emp-country"
                className="asInput"
                name="country"
                value={formData.country}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="asCard" aria-label="Works at locations">
        <div className="asCardBody">
          <LocationAssignmentField
            locations={locations}
            value={formData.locationIds}
            onChange={(locationIds) => setFormData((prev) => ({ ...prev, locationIds }))}
            disabled={saving}
            label="Works at locations"
            helperText="Where this team member can be scheduled. They must work at a location and be assigned to a service for online booking to show times."
          />
        </div>
      </section>

      <div className="asActions">
        <span className="asSavingNote">{saving ? 'Saving changes…' : ''}</span>
        <button type="button" className="asBtnSecondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <PrimaryButton
          onClick={handleSubmit}
          disabled={saving || !formData.firstName || !formData.lastName}
          style={{ width: 220, pointerEvents: saving ? 'none' : 'auto' }}
        >
          {saving ? 'Saving…' : 'Save profile'}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default EmployeeProfileEditor;
